import React, { FC,useEffect, useState, useRef } from 'react';
import superHostImage from 'assets/svg/superhost-user-key-indicator.svg';
import locationIcon from 'assets/svg/location.svg';
import Styles from 'components/chatContent/chatInfo/ChatInfo.module.scss';
import CustomModal from 'components/customModal/CustomModal';
import Button from 'components/button/Button';
import LightBox from '../../lightBox/LightBox';
import { IMAGE_URL } from 'store/services/URL';
import { deleteParticipants, getRoomLocation, uploadIntroVideo, updateRoomIntroVideoInFirebase } from 'store/services/firebase-broadcast';
import { reference, deleteObject, storage } from 'utils/firebase-config';
import { api } from 'store/services/conversation.service';
import minus from 'assets/images/minus.png';
import Toast from 'components/toast/Tost';
import { useDispatch } from 'react-redux';
import { openImageViewer } from 'store/reducers/imageViewer.reducer';
import { getParticipantsArray, getParticipantsCount, validateIntroVideoSize } from 'helpers/utils';
import VideoPlayer from 'components/videoPlayer/VideoPlayer';

const MAX_MEDIA_DISPLAY = 12;

interface ChatInfoProps {
  cssClass?: string;
  info: any;
  setSelected?: any;
  allRooms?: any;

  isJoined?: boolean;
  onClose?: () => void;
  isDrawerOpen?: boolean;
}

const ChatInfo: FC<ChatInfoProps> = (props: ChatInfoProps) => {
  const { cssClass, info, allRooms, setSelected, isJoined, onClose, isDrawerOpen } = props;
  const [viewParticipants, setViewParticipants] = useState(false);
  const [viewMedia, setViewMedia] = useState(false);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [index, setIndex] = useState(0);
  const [toast, setToast] = useState(false);
  const [toastValues, setToastValues] = useState({message: '', type: ''});
  const [deleteRoom, setDeleteRoom] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dispatch = useDispatch();

  // Validated media list (only successfully loading images)
  const [mediaAll, setMediaAll] = useState<string[]>([]);

  // Get location data directly from Firebase room data (no API calls needed)
  const locationInfo = getRoomLocation(info);

  // Preload images and keep only valid URLs
  const loadValidImages = async (urls: string[]) => {
    const results = await Promise.all(
      urls.map(
        (u) =>
          new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = u;
          })
      )
    );
    return urls.filter((_, i) => results[i]);
  };

  useEffect(() => {
    const arr = Array.isArray(info?.attachments) ? info.attachments : [];
    const urls = arr.filter(
      (item: any) => item && typeof item === 'string' && (item.startsWith('http') || item.startsWith('blob:'))
    );
    loadValidImages(urls).then(setMediaAll);
  }, [info?.attachments]);

  // Reset upload state when room changes
  useEffect(() => {
    setIsUploadingVideo(false);
    // Reset the file input when switching rooms
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  }, [info?.pk]);

  const openParticipants = () => {
    document.body.classList.add('modal-open');
    setViewParticipants(true);
  };

  const photosAndMedia = () => {
    document.body.classList.add('modal-open');
    setViewMedia(true);
  };

  const closeDialog = () => {
    document.body.classList.remove('modal-open');
    setViewParticipants(false);
    setViewMedia(false);
    setOpenLightBox(false);
  };

  const viewAllParticipants = () => (<>  
      <div className={Styles.viewAllWrapper}>
        {getParticipantsArray(info?.participants).map((item: any, index: number) => {
          // Find the Firebase key for this participant (same logic as main list)
          const participantsObj = info?.participants || {};
          const firebaseKey = Array.isArray(info?.participants) 
            ? index 
            : Object.keys(participantsObj).find(key => participantsObj[key]?.pk === item.pk) || index;
          
          return (
            <div className={`text-center ${Styles.viewAllInner}`} key={item.pk || firebaseKey}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={`${IMAGE_URL}${item.profile}`} alt="participants" loading="lazy" />
                {/* Add kick button for host */}
                {info?.host?.pk === user?.pk && (
                  <img
                    onClick={() => handleDelete(item, firebaseKey)} 
                    onKeyUp={() => handleDelete(item, firebaseKey)}
                    src={minus}
                    alt="remove participant"
                    className={Styles.superHostImg}
                  />
                )}
              </div>
              <p className="my-0 fontThirteen font-weight-normal text-black">{item.name}</p>
            </div>
          );
        })}
      </div>
      <div className="text-center">
        <Button label="close" cssClass={Styles.closeBtn} clickHandler={closeDialog} />
      </div>
    </>);

  const getVisibleMediaCount = () => mediaAll.length;

  const ViewAllMedia = () => (<>
      <div className={Styles.customHeader}>
        <h3>Photos & Media</h3>
        <p className={Styles.tagLine}>{mediaAll.length} photos & 0 video</p>
      </div>
      <div className={Styles.allMedia}>
        {(mediaAll && mediaAll.length > 0)
          ? mediaAll.map((item: any, i: number) => (
            <div id={`potot-${i}`} className={Styles.allMediaInner} key={i}>
              <div
                className={Styles.mediaTileWrapper}
                      onClick={() => dispatch(openImageViewer({ imageUrl: item, imageName: `Image ${i + 1}` }))}
                      onKeyPress={() => dispatch(openImageViewer({ imageUrl: item, imageName: `Image ${i + 1}` }))}
                role="button"
                tabIndex={0}
              >
                <img 
                  src={item} 
                  alt="media"
                />
              </div>
            </div>
          )) : null}
      </div>
      <div className="text-center mt-5">
        <Button label="close" cssClass={Styles.closeBtn} clickHandler={closeDialog} />
      </div>
    </>);

    useEffect(() => {
      if (deleteRoom) {
        let updatedRoom = allRooms.find(function(e: any) {
          return e.pk == info.pk;
        })
        if (updatedRoom) {
          setSelected(updatedRoom)
        }
        setDeleteRoom(false) // Fix: Reset to false to prevent infinite loop
      }
    }, [deleteRoom, allRooms, info.pk, setSelected])

  const handleDelete = (item: any, firebaseKey: string | number) => {
    const data = {
      roomId: info?.pk,
      participantId: firebaseKey, // Use the actual Firebase key
    };
    deleteParticipants(data).then((e) => {
      setDeleteRoom(true)
      setToast(true);
      setToastValues({
        message: "User has been removed.",
        type: "Success",
      });
    }).catch(() => {
      setToastValues({
        message: 'Something went wrong!',
        type: 'Error',
      });
      setToast(true);
    });
    return
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate video file (type and size)
      const validationError = validateIntroVideoSize(file);
      if (validationError) {
        setToast(true);
        setToastValues({
          message: validationError,
          type: 'Error',
        });
        return;
      }
      
      // Upload new video
      uploadAndUpdateVideo(file);
    }
  };

  const uploadAndUpdateVideo = async (file: File) => {
    setIsUploadingVideo(true);
    let videoRef: any = null;
    let videoUrl: string | null = null;
    
    try {
      // Upload to Firebase Storage
      const uploadResult = await uploadIntroVideo(info?.pk, file);
      videoUrl = uploadResult.videoUrl;
      videoRef = uploadResult.videoRef;
      
      // Update Django backend
      await api.updateRoomIntroVideo({
        id: info?.pk,
        intro_video_url: videoUrl,
      });
      
      // Sync to Firebase Realtime Database
      await updateRoomIntroVideoInFirebase(info?.pk, videoUrl);
      
      // Optimistically update selected state for immediate UI feedback
      if (setSelected) {
        setSelected((prevSelected: any) => {
          if (prevSelected?.pk === info?.pk) {
            return {
              ...prevSelected,
              intro_video_url: videoUrl,
            };
          }
          return prevSelected;
        });
      }
      
      setToast(true);
      setToastValues({
        message: 'Intro video updated successfully.',
        type: 'Success',
      });
      
      // The Firebase update will trigger useGetRooms() to refresh automatically
      setDeleteRoom(true);
    } catch (error: any) {
      // If upload succeeded but backend update failed, clean up the uploaded file
      if (videoRef && videoUrl) {
        try {
          await deleteObject(videoRef);
          console.log('Cleaned up orphaned video file after backend update failure');
        } catch (deleteError) {
          console.error('Error cleaning up orphaned video file:', deleteError);
          // Don't throw - we still want to show the error to the user
        }
      }
      
      setToast(true);
      setToastValues({
        message: error?.message || 'Failed to update video. Please try again.',
        type: 'Error',
      });
    } finally {
      setIsUploadingVideo(false);
      // Reset input
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleRemoveVideo = async () => {
    try {
      // Delete from Firebase Storage if URL exists
      if (info?.intro_video_url) {
        try {
          // Extract path from Firebase Storage URL
          const urlObj = new URL(info.intro_video_url);
          const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
          if (pathMatch) {
            const decodedPath = decodeURIComponent(pathMatch[1]);
            const videoRef = reference(storage, decodedPath);
            await deleteObject(videoRef);
          }
        } catch (deleteError) {
          console.error('Error deleting video from storage:', deleteError);
          // Continue even if deletion fails
        }
      }
      
      // Update Django backend
      await api.updateRoomIntroVideo({
        id: info?.pk,
        intro_video_url: null as any,
      });
      
      // Sync to Firebase Realtime Database
      await updateRoomIntroVideoInFirebase(info?.pk, null);
      
      // Optimistically update selected state for immediate UI feedback
      if (setSelected) {
        setSelected((prevSelected: any) => {
          if (prevSelected?.pk === info?.pk) {
            return {
              ...prevSelected,
              intro_video_url: null,
            };
          }
          return prevSelected;
        });
      }
      
      setToast(true);
      setToastValues({
        message: 'Intro video removed successfully.',
        type: 'Success',
      });
      
      // The Firebase update will trigger useGetRooms() to refresh automatically
      setDeleteRoom(true);
    } catch (error: any) {
      setToast(true);
      setToastValues({
        message: error?.message || 'Failed to remove video. Please try again.',
        type: 'Error',
      });
    }
  };

  return (
    <div className={`${Styles.messageThreadContainer} ${isDrawerOpen ? Styles.drawerOpen : ''}`}>
      <div className={`${Styles.messageThreadWrapper} ${cssClass} customScrollBar`}>
        {onClose && (
          <button
            className={Styles.drawerCloseButton}
            onClick={onClose}
            aria-label="Close info panel"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
        <div className={`text-center ${Styles.userDetails}`}>
          <div className={`position-relative ${Styles.userImageWrapper}`}>
            <img src={`${IMAGE_URL}${info?.host?.profile}`} alt="user img" loading="lazy" />
            {/* {info?.host?.is_super_host && <img src={superHostImage} alt="super host" className={Styles.superHostImage} />} */}
          </div>
          <h5 className="text-black fontSixteen font-weight-600 my-0">{info?.topic}</h5>
          <span className="text-uppercase input-color fontFifteen font-weight-normal">{info?.host?.name}&apos;s Room</span>
          
          {/* Location Information - only show if not both Unknown */}
          {locationInfo && locationInfo.city !== "Unknown" && locationInfo.country !== "Unknown" && (
            <div className={Styles.locationInfo}>
              <span className="text-muted fontThirteen font-weight-normal">
                <img src={locationIcon} alt="location" className={Styles.locationIcon} />
                {[locationInfo.city, locationInfo.country].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
        <div className={Styles.chatParticipants}>
          <h6 className="fontSixteen font-weight-normal text-black">Chat Participants ({getParticipantsCount(info?.participants)})</h6>
          <div className={Styles.participantsWrapper}>
            { getParticipantsArray(info?.participants).slice(0, 10).map((item: any, index: number) => {
              // Find the Firebase key for this participant
              const participantsObj = info?.participants || {};
              const firebaseKey = Array.isArray(info?.participants) 
                ? index 
                : Object.keys(participantsObj).find(key => participantsObj[key]?.pk === item.pk) || index;
              
              return (
                <div key={item.pk || firebaseKey}>
                  <img src={`${IMAGE_URL}${item.profile}`} alt="participants" loading="lazy" role="button"/>
                  {
                    info?.host?.pk === user?.pk && (
                      <img
                        onClick={() => handleDelete(item, firebaseKey)} 
                        onKeyUp={() => handleDelete(item, firebaseKey)}
                        src={minus}
                        alt="super host"
                        className={Styles.superHostImg}/>
                    )
                  }
                </div>
              );
            })} 
          </div>
          {getParticipantsCount(info?.participants) > 0 && (
            <div onClick={openParticipants} onKeyPress={openParticipants} role="button" tabIndex={0}>
              <p className="text-blue font-weight-bold my-3 fontThirteen">View All ({getParticipantsCount(info?.participants)})</p>
            </div>
          )}
        </div>
        <div className={Styles.photosAndMedia}>
          <h6 className="fontSixteen font-weight-normal text-black">Photos & Media ({getVisibleMediaCount()})</h6>
          
          {isJoined ? (
            <div className={Styles.photosMediaWrapper}>
              {mediaAll.length > 0 && (
                mediaAll
                  .slice(0, MAX_MEDIA_DISPLAY)
                  .map((item: any, i: number) => (
                    <div id={`potot-${i}`} className={Styles.allMediaInner} key={i}>
                      <div
                        className={Styles.mediaTileWrapper}
                        onClick={() => dispatch(openImageViewer({ imageUrl: item, imageName: `Image ${i + 1}` }))}
                        onKeyPress={() => dispatch(openImageViewer({ imageUrl: item, imageName: `Image ${i + 1}` }))}
                        role="button"
                        tabIndex={0}
                      >
                        <img 
                          src={item} 
                          alt="media" 
                          loading="lazy"
                        />
                      </div>
                    </div>
                  ))
              )}
            </div>
          ) : (
            <div className="mt-2">
              <p className="text-muted font-weight-normal fontThirteen">Join this room to view.</p>
            </div>
          )}
        </div>
        {mediaAll.length > 0 && (
          <div onClick={photosAndMedia} onKeyPress={photosAndMedia} role="button" tabIndex={0}>
            <p className="text-blue font-weight-bold my-3 fontThirteen">View All</p>
          </div>
        )}
        {/* Intro Video Section - Show for host (to upload) or when video exists (for all joined users) */}
        {isJoined && (info?.host?.pk === user?.pk || info?.intro_video_url) && (
          <div className={Styles.introVideoSection}>
            <div className={Styles.sectionHeader}>
              <h6 className="fontSixteen font-weight-normal text-black">Intro Video</h6>
              {info?.host?.pk === user?.pk && (
                <div className={Styles.videoActions}>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoSelect}
                    style={{ display: 'none' }}
                  />
                  {info?.intro_video_url ? (
                    // Show Update/Remove buttons when video exists
                    <>
                      <button
                        onClick={() => videoInputRef.current?.click()}
                        className={Styles.editButton}
                        disabled={isUploadingVideo}
                        type="button"
                      >
                        {isUploadingVideo ? 'Uploading...' : 'Update'}
                      </button>
                      <button
                        onClick={handleRemoveVideo}
                        className={Styles.removeButton}
                        disabled={isUploadingVideo}
                        type="button"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    // Show Add button when no video exists
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className={Styles.addButton}
                      disabled={isUploadingVideo}
                      type="button"
                    >
                      {isUploadingVideo ? 'Uploading...' : 'Add'}
                    </button>
                  )}
                </div>
              )}
            </div>
            {info?.intro_video_url && (
              <div className={Styles.introVideoWrapper}>
                <VideoPlayer url={info.intro_video_url} maxHeight="200px" />
              </div>
            )}
            {!info?.intro_video_url && info?.host?.pk === user?.pk && (
              <div className={Styles.noVideoPlaceholder}>
                <p className="text-muted font-weight-normal fontThirteen">
                  No intro video added yet. Click &quot;Add Intro Video&quot; to upload one.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {viewParticipants && (
        <CustomModal title="Chat Participants" wrapperClass={Styles.wrapperClass} closeDialog={closeDialog}>
          {viewAllParticipants()}
        </CustomModal>
      )}
      {viewMedia && (
        <CustomModal wrapperClass={Styles.viewMediaWrapper} closeDialog={closeDialog}>
          {ViewAllMedia()}
        </CustomModal>
      )}
      {openLightBox && <div>Marjao</div> && <LightBox data={info.attachments} index={index} closeDialog={closeDialog} />}
      {toast && (
        <Toast
          message={toastValues.message}
          position="topRight"
          type={toastValues.type}
          handleCloseToast={setToast}
        />
      )}
    </div>
  );
};
export default ChatInfo;
